/** XeAgents.ai**/
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Plus, Loader2, Pencil, Trash2, DollarSign, Package } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoTooltip } from "@/components/ui/info-tooltip";

interface CreditPackage {
  id: string;
  name: string;
  description: string;
  country: string;
  credits: number;
  price: string;
  razorpayPrice: string | null;
  stripeProductId: string | null;
  stripePriceId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreditPackageFormData {
  name: string;
  description: string;
  country: string;
  credits: number;
  price: number;
  razorpayPrice: number | null;
  isActive: boolean;
}

export default function CreditPackageManagement() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(null);
  const [deletingPackage, setDeletingPackage] = useState<CreditPackage | null>(null);
  const [formData, setFormData] = useState<CreditPackageFormData>({
    name: "",
    description: "",
    country: "US",
    credits: 100,
    price: 0,
    razorpayPrice: null,
    isActive: true,
  });

  const { data: packages, isLoading } = useQuery<CreditPackage[]>({
    queryKey: ["/api/admin/credit-packages", { app: 'xevoicepro' }],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/credit-packages?app=xevoicepro');
      if (!res.ok) throw new Error('Failed to fetch credit packages');
      return res.json();
    },
  });

  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  // Filter by country (exclude XeHirePro packages)
  const filteredPackages = (packages || []).filter(
    (pkg) => pkg.country === selectedCountry && !pkg.name.startsWith('XeHirePro')
  );

  const createPackage = useMutation({
    mutationFn: async (data: CreditPackageFormData) => {
      return apiRequest("POST", "/api/admin/credit-packages", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({
        title: "Package Created",
        description: "The credit package has been created successfully",
      });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Create Package",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePackage = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreditPackageFormData> }) => {
      return apiRequest("PATCH", `/api/admin/credit-packages/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({
        title: "Package Updated",
        description: "The credit package has been updated successfully",
      });
      setIsEditDialogOpen(false);
      setEditingPackage(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Update Package",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deletePackage = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/credit-packages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({
        title: "Package Deleted",
        description: "The credit package has been deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Delete Package",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      country: "US",
      credits: 100,
      price: 0,
      razorpayPrice: null,
      isActive: true,
    });
  };

  const handleEdit = (pkg: CreditPackage) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      country: pkg.country || "US",
      credits: pkg.credits,
      price: parseFloat(pkg.price),
      razorpayPrice: pkg.razorpayPrice ? parseFloat(pkg.razorpayPrice) : null,
      isActive: pkg.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (pkg: CreditPackage) => {
    setDeletingPackage(pkg);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (deletingPackage) {
      await deletePackage.mutateAsync(deletingPackage.id);
      setIsDeleteDialogOpen(false);
      setDeletingPackage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate currency-specific prices
    const isIndia = formData.country === 'IN';
    
    if (isIndia) {
      // INR package: require INR price (razorpay price)
      if (!formData.razorpayPrice || formData.razorpayPrice <= 0) {
        toast({
          title: 'Validation Error',
          description: 'Price in INR is required for India packages',
          variant: 'destructive',
        });
        return;
      }
    } else {
      // USD package: require USD price
      if (!formData.price || formData.price <= 0) {
        toast({
          title: 'Validation Error',
          description: 'Price in USD is required for US packages',
          variant: 'destructive',
        });
        return;
      }
    }
    
    if (editingPackage) {
      await updatePackage.mutateAsync({ id: editingPackage.id, data: formData });
    } else {
      await createPackage.mutateAsync(formData);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-40">
                <Select
                  value={selectedCountry}
                  onValueChange={(value) => setSelectedCountry(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="IN">India</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Credit Packages
              </CardTitle>
              <CardDescription>
                Create and manage credit packages for one-time purchases
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Package
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPackages.map((pkg) => (
              <Card key={pkg.id} className={`relative ${!pkg.isActive ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                      <CardDescription className="mt-1">{pkg.description}</CardDescription>
                    </div>
                  </div>
                  {!pkg.isActive && (
                    <div className="mt-2">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                        Inactive
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">{pkg.credits.toLocaleString()} credits</span>
                  </div>
                  {(() => {
                    const isIndia = selectedCountry === "IN";
                    const displayPrice = isIndia ? pkg.razorpayPrice : pkg.price;
                    const currencySymbol = isIndia ? "₹" : "$";
                    const currencyCode = isIndia ? "INR" : "USD";

                    return (
                      <>
                        <div className="flex items-baseline gap-1">
                          <DollarSign className="h-4 w-4 text-muted-foreground mt-1" />
                          <span className="text-2xl font-bold">
                            {displayPrice
                              ? `${currencySymbol}${parseFloat(displayPrice).toFixed(2)}`
                              : "—"}
                          </span>
                          <span className="text-muted-foreground text-sm">{currencyCode}</span>
                        </div>
                        {displayPrice && (
                          <div className="text-xs text-muted-foreground">
                            {currencySymbol}{(parseFloat(displayPrice) / pkg.credits).toFixed(4)} per credit
                          </div>
                        )}
                      </>
                    );
                  })()}
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(pkg)}
                      className="flex-1"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(pkg)}
                      className="flex-1 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredPackages.length === 0 && (
            <Alert>
              <AlertDescription className="text-center py-8">
                No credit packages found. Create your first package to get started.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          setEditingPackage(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Credit Package' : 'Create New Credit Package'}</DialogTitle>
            <DialogDescription>
              {editingPackage ? 'Update the credit package details' : 'Configure a new credit package for one-time purchases'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold">Package Information</h4>
              
              <div>
                <Label>Package Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Starter Pack, Growth Pack"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Display name for the credit package
                </p>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the package and its value"
                  required
                />
              </div>

              <div>
                <Label>Country</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="IN">India</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Credits and Pricing */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Credits & Pricing</h4>
                <div className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">
                  {formData.country === 'IN' ? '₹ INR Package' : '$ USD Package'}
                </div>
              </div>
              
              {formData.country === 'US' && (
                <div className="p-3 rounded bg-blue-50 border border-blue-200 text-sm text-blue-900">
                  <strong>USD Package:</strong> Enter price in US Dollars. This will be synced to Stripe.
                </div>
              )}
              
              {formData.country === 'IN' && (
                <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <strong>INR Package:</strong> Enter price in Indian Rupees. This will be synced to Razorpay.
                </div>
              )}
              
              <div>
                <Label>Credits</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value, 10) || 0 })}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Number of credits included in this package
                </p>
              </div>

              {formData.country !== 'IN' && (
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Price ($) <span className="text-red-500">*</span></Label>
                    <InfoTooltip content="Price in USD (required for US packages)" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    placeholder="Required for US packages"
                    className={formData.price <= 0 ? 'border-red-500' : ''}
                  />
                  {formData.price <= 0 && (
                    <p className="text-xs text-red-500 mt-1">Required for US packages</p>
                  )}
                </div>
              )}

              {formData.country === 'IN' && (
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Price (₹ INR) <span className="text-red-500">*</span></Label>
                    <InfoTooltip content="Price in Indian Rupees (Razorpay)" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.razorpayPrice || 0}
                    onChange={(e) => setFormData({ ...formData, razorpayPrice: parseFloat(e.target.value) || null })}
                    placeholder="Required for India packages"
                    className={(formData.razorpayPrice === null || formData.razorpayPrice <= 0) ? 'border-red-500' : ''}
                  />
                  {(formData.razorpayPrice === null || formData.razorpayPrice <= 0) && (
                    <p className="text-xs text-red-500 mt-1">Required for India packages</p>
                  )}
                </div>
              )}

              {formData.credits > 0 && (
                <>
                  {formData.country !== 'IN' && formData.price > 0 && (
                    <div className="p-3 bg-muted rounded-md">
                      <p className="text-sm">
                        <span className="font-semibold">Price per credit:</span>{' '}
                        ${(formData.price / formData.credits).toFixed(4)}
                      </p>
                    </div>
                  )}
                  {formData.country === 'IN' && formData.razorpayPrice && formData.razorpayPrice > 0 && (
                    <div className="p-3 bg-muted rounded-md">
                      <p className="text-sm">
                        <span className="font-semibold">Price per credit:</span>{' '}
                        ₹{(formData.razorpayPrice / formData.credits).toFixed(4)}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h4 className="font-semibold">Status</h4>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked as boolean })}
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Active (visible to customers)
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                  setEditingPackage(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createPackage.isPending || updatePackage.isPending}>
                {(createPackage.isPending || updatePackage.isPending) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editingPackage ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingPackage ? 'Update Package' : 'Create Package'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Credit Package</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the &quot;{deletingPackage?.name}&quot; package? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deletePackage.isPending}>
              {deletePackage.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}